/**
 * CTC-AI-Robot 二期「查询数据」工具层——纯逻辑部分（无 API/网络依赖，便于单测）。
 *
 * 设计见 workspace `docs/product/features/2026-07-04-ai-robot-phase2-data-query-tools.md`。
 * 具体工具执行器（包装 CTC 已有只读接口）在 `tool-registry.ts`，以隔离 Vite 专有依赖。
 * 模型只能调用注册表里的工具（白名单），不能调任意接口。
 */

/** 工具循环最大迭代次数，防止模型反复调工具死循环。 */
export const MAX_TOOL_ITERATIONS = 5;

export interface ToolDef {
  type: 'function';
  function: {
    name: string;
    description: string;
    parameters: {
      type: 'object';
      properties: Record<string, any>;
      required?: string[];
    };
  };
}

export interface ToolCall {
  id: string;
  name: string;
  args: Record<string, any>;
}

/** 工具执行上下文：由对话组件注入，携带非模型参数（如当前会话 ID，用于工单溯源）。 */
export interface ToolContext {
  conversationId?: string;
}

export type ToolExecutor = (args: Record<string, any>, ctx?: ToolContext) => Promise<string>;

/** 暴露给模型的工具 schema（OpenAI function-calling 格式）。纯数据。 */
export const CTC_ROBOT_TOOLS: ToolDef[] = [
  {
    type: 'function',
    function: {
      name: 'query_issues',
      description:
        '通用缺陷(issue)查询：可组合筛选，返回计数或清单。筛选项都可选可组合：projectName 项目名、version 版本名(按版本筛需同时给 projectName)、status 状态、priority 优先级、createdFrom/createdTo 按创建时间(YYYY-MM-DD)、keyword 标题/描述关键词、mine(我提交/我经办)。mode=count 返回数量(总数/已关闭/挂起/未关闭)，mode=list 返回缺陷清单(编号/标题/状态)。只读。',
      parameters: {
        type: 'object',
        properties: {
          projectName: { type: 'string', description: '项目名称，可空=全部有权限项目' },
          version: {
            type: 'string',
            description: '版本名（如"固件-48.01.005"或其片段），按版本筛时必须同时给 projectName（版本隶属项目）；可空',
          },
          status: {
            type: 'array',
            items: { type: 'string' },
            description:
              '状态名，如 ["未关闭"] 或 ["处理中","挂起"]；可用值：未关闭/新创建/处理中/已解决/关闭/拒绝/挂起（"未关闭"=新创建+处理中+已解决）',
          },
          priority: {
            type: 'array',
            items: { type: 'string' },
            description: '优先级名，可用值：最高/高/中/低，如 ["高","最高"]',
          },
          createdFrom: { type: 'string', description: '创建时间起 YYYY-MM-DD，可空' },
          createdTo: { type: 'string', description: '创建时间止 YYYY-MM-DD，可空' },
          keyword: { type: 'string', description: '标题/描述关键词，可空' },
          mine: {
            type: 'string',
            enum: ['reporter', 'assignee'],
            description: 'reporter=我提交的 / assignee=我经办的，可空',
          },
          mode: {
            type: 'string',
            enum: ['count', 'list'],
            description: 'count=只要数量，list=要缺陷清单；默认 count',
          },
        },
        required: [],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'query_issue_stats',
      description:
        '缺陷分布统计：按状态(status)、优先级(priority)、严重程度(severity)、经办人(assignee)或部门(dept)统计各档数量。经办人/部门维度还返回每人每部门的未关闭数(团队负载)。可选按项目名、创建时间范围筛选。优先级与严重程度是不同字段，别混用。只读。',
      parameters: {
        type: 'object',
        properties: {
          dimension: {
            type: 'string',
            enum: ['status', 'priority', 'severity', 'assignee', 'dept'],
            description:
              '分布维度：status=按状态，priority=按优先级，severity=按严重程度，assignee=按经办人(含各人未关闭数)，dept=按部门；默认 status',
          },
          projectName: { type: 'string', description: '项目名称，可空' },
          version: { type: 'string', description: '版本名（按版本筛需同时给 projectName），可空' },
          createdFrom: { type: 'string', description: '创建时间起 YYYY-MM-DD，可空' },
          createdTo: { type: 'string', description: '创建时间止 YYYY-MM-DD，可空' },
        },
        required: [],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'query_issue_trend',
      description:
        '缺陷随时间的趋势：返回各时间桶的新增数(createCount)、关闭数(closedCount)、重开数(reOpenCount)，用于回答"每月/每周缺陷新增与关闭趋势""什么时候提得多"。可选按项目名、版本、创建时间范围筛选。只读。',
      parameters: {
        type: 'object',
        properties: {
          projectName: { type: 'string', description: '项目名称，可空' },
          version: { type: 'string', description: '版本名（需同时给 projectName），可空' },
          timeDimension: {
            type: 'string',
            enum: ['day', 'week', 'month'],
            description: '时间粒度：day/week/month；默认 month',
          },
          createdFrom: { type: 'string', description: '起始时间 YYYY-MM-DD，可空' },
          createdTo: { type: 'string', description: '结束时间 YYYY-MM-DD，可空' },
        },
        required: [],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'query_test_plan_progress',
      description:
        '查询研发项目的测试计划执行进度（用例维度）：返回项目下各测试计划的总用例数/已执行/通过/失败/阻塞/未执行及执行率、通过率。必须给研发项目名 projectName（此为研发项目管理的项目，与缺陷项目是不同体系）；可选 planName 只看某个计划。只读。',
      parameters: {
        type: 'object',
        properties: {
          projectName: { type: 'string', description: '研发项目名称（必填，如 CyberPi、Corbit）' },
          planName: { type: 'string', description: '测试计划名称，模糊匹配；不给则返回该项目下全部计划的进度' },
        },
        required: ['projectName'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'query_iqc_receive_bills',
      description:
        '查询 IQC 来料检验/收料单：可按物料名称、物料编号、单号、状态、收料时间范围筛选，返回单据总数与前几条摘要。只读。',
      parameters: {
        type: 'object',
        properties: {
          materialName: { type: 'string', description: '物料名称，可空' },
          materialNumber: { type: 'string', description: '物料编号，可空' },
          status: { type: 'string', description: '检验单状态，可空' },
          billNo: { type: 'string', description: '收料单号，可空' },
          createdFrom: { type: 'string', description: '收料时间起 YYYY-MM-DD，可空' },
          createdTo: { type: 'string', description: '收料时间止 YYYY-MM-DD，可空' },
        },
        required: [],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_issue_detail',
      description: '按缺陷编号(issueKey，如 CTC-123)查询单个缺陷(issue)的详情。只读。',
      parameters: {
        type: 'object',
        properties: {
          issueKey: { type: 'string', description: '缺陷编号，如 CTC-123' },
        },
        required: ['issueKey'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'query_wms_receive_bills',
      description:
        '查询 WMS 收料单：可按物料名称、物料编号、单号、状态、收料时间范围筛选，返回单据总数与前几条摘要。只读。',
      parameters: {
        type: 'object',
        properties: {
          materialName: { type: 'string', description: '物料名称，可空' },
          materialNumber: { type: 'string', description: '物料编号，可空' },
          status: { type: 'string', description: '收料单状态，可空' },
          billNo: { type: 'string', description: '收料单号，可空' },
          createdFrom: { type: 'string', description: '收料时间起 YYYY-MM-DD，可空' },
          createdTo: { type: 'string', description: '收料时间止 YYYY-MM-DD，可空' },
        },
        required: [],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'search_similar_issues',
      description:
        '缺陷查重：按一个关键词/短语搜索标题或描述包含它的已有缺陷，返回候选清单用于判断是否重复。想扩大召回可用不同关键词多次调用。可选按项目名限定。只读。',
      parameters: {
        type: 'object',
        properties: {
          keyword: {
            type: 'string',
            description: '单个关键词或短语（从缺陷标题/步骤里提取的核心词，如“闪退”“导入角色”）',
          },
          projectName: { type: 'string', description: '项目名称，可空；为空则跨全部项目查重' },
        },
        required: ['keyword'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'save_work_item',
      description:
        '把已聊清楚的需求或 bug 记录入库，进入管理员审核队列。这是唯一的写操作，**必须先向用户复述要点并获得用户明确同意后才调用**（用户没说“提交/可以”就不要调）。type=feature 时 spec 重点填 acceptanceCriteria/affectedRepos/affectedFiles；type=bug 时重点填 reproduction(复现步骤+期望vs实际)/affectedFiles(模块/页面)。调用成功即完成提交，之后如实告知编号，不必再贴长文档。',
      parameters: {
        type: 'object',
        properties: {
          type: { type: 'string', enum: ['bug', 'feature'], description: 'bug=缺陷 / feature=功能需求' },
          title: { type: 'string', description: '简洁标题' },
          rawText: { type: 'string', description: '用户最初的原话/核心诉求原文（溯源用）' },
          spec: {
            type: 'object',
            description: '结构化内容，按已聊清的信息填，未知留空不要编造',
            properties: {
              summary: { type: 'string', description: '一句话描述' },
              acceptanceCriteria: {
                type: 'array',
                items: { type: 'string' },
                description: '验收标准（需求重点，写成可勾选项）',
              },
              affectedRepos: {
                type: 'array',
                items: { type: 'string' },
                description: '影响仓：mfd-server / accurate-server / ctc-next',
              },
              affectedFiles: { type: 'array', items: { type: 'string' }, description: '涉及文件/页面/模块，尽量具体' },
              testPlan: {
                type: 'string',
                description: '怎么验证算做完（必填，写清验证方式：跑哪个测试/手动走哪条流程看什么结果）',
              },
              constraints: { type: 'string', description: '约束：不能改什么、要复用什么' },
              reproduction: { type: 'string', description: 'bug 专用：复现步骤 + 期望结果 vs 实际结果' },
            },
          },
        },
        required: ['type', 'title'],
      },
    },
  },
];

/** 从网关返回的 assistant message 中解析出规范化的工具调用列表。 */
export function parseToolCalls(message: any): ToolCall[] {
  const raw = message?.tool_calls;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map((tc: any) => {
    let args: Record<string, any> = {};
    try {
      args = tc?.function?.arguments ? JSON.parse(tc.function.arguments) : {};
    } catch {
      args = {};
    }
    return { id: tc?.id, name: tc?.function?.name, args };
  });
}

export type ResponseAction = { kind: 'tools'; calls: ToolCall[] } | { kind: 'final'; content: string };

/** 判断这轮网关响应是要调工具、还是已经给出最终答复。 */
export function interpretResponse(message: any): ResponseAction {
  const calls = parseToolCalls(message);
  if (calls.length > 0) {
    return { kind: 'tools', calls };
  }
  return { kind: 'final', content: message?.content ?? '' };
}

/**
 * 执行一次工具调用，返回给模型的字符串结果（role:"tool" 的 content）。
 * 未注册工具 / 执行异常都转成 error 字符串，绝不抛出——由模型如实转述给用户。
 */
export async function executeToolCall(
  call: ToolCall,
  registry: Record<string, ToolExecutor>,
  ctx?: ToolContext,
): Promise<string> {
  const fn = registry[call.name];
  if (!fn) {
    return JSON.stringify({ error: `未注册的工具：${call.name}` });
  }
  try {
    return await fn(call.args ?? {}, ctx);
  } catch (e: any) {
    return JSON.stringify({ error: String(e?.message ?? e) });
  }
}

/**
 * 由本轮工具结果构建结构化报告卡片数据（纯函数，供 report-card 渲染）。
 * 不依赖 LLM 输出，直接读工具返回的真实结构，故可靠、不劣化对话。
 * 只有出现分布(bar)或 ≥2 个数据段时才出卡片（单一简单查询不出，避免噪音）。
 */
const DIM_LABEL: Record<string, string> = {
  status: '状态分布',
  priority: '优先级分布',
  severity: '严重程度分布',
  assignee: '经办人负载',
  dept: '部门分布',
};
function safeParse(v: any): any {
  if (v && typeof v === 'object') {
    return v;
  }
  try {
    return JSON.parse(v);
  } catch {
    return null;
  }
}
export interface ToolResultLite {
  name: string;
  data: any;
}
export function buildReportCard(results: ToolResultLite[]): { title: string; sections: any[] } | null {
  const sections: any[] = [];
  let title = '';
  let hasBar = false;
  for (const r of results) {
    const data = safeParse(r.data);
    if (!data || data.error) {
      continue;
    }
    if (r.name === 'query_issues' && data.mode !== 'list' && data.total !== undefined) {
      const scope = data.scope || '';
      if (!title && scope) {
        title = `${scope} 缺陷报告`;
      }
      const total = data.total ?? 0;
      const closed = data.closed ?? 0;
      const rate = total > 0 ? Math.round((closed / total) * 1000) / 10 : 0;
      sections.push({
        label: scope ? `总览 · ${scope}` : '总览',
        type: 'kv',
        items: [
          { k: '总数', v: String(total) },
          { k: '已关闭', v: String(closed) },
          { k: '未关闭', v: String(data.unclosed ?? total - closed) },
          { k: '关闭率', v: `${rate}%` },
        ],
      });
    } else if (r.name === 'query_issue_stats' && Array.isArray(data.distribution)) {
      const dim = data.dimension || 'status';
      const label = DIM_LABEL[dim] || '分布';
      if (!title && data.scope) {
        title = `${data.scope} 缺陷报告`;
      }
      if (dim === 'assignee' || dim === 'dept') {
        sections.push({
          label,
          type: 'kv',
          items: data.distribution.slice(0, 12).map((d: any) => ({
            k: d.name,
            v: `${d.count}${typeof d.unclosed === 'number' ? `（未关闭${d.unclosed}）` : ''}`,
          })),
        });
      } else {
        hasBar = true;
        sections.push({
          label,
          type: 'bar',
          items: data.distribution.map((d: any) => ({ name: d.name, count: d.count })),
        });
      }
    } else if (r.name === 'query_issue_trend' && Array.isArray(data.trend)) {
      sections.push({
        label: '趋势（新增/关闭）',
        type: 'kv',
        items: data.trend.slice(-6).map((t: any) => ({ k: t.date, v: `+${t.created ?? 0} / -${t.closed ?? 0}` })),
      });
    } else if (r.name === 'query_test_plan_progress' && Array.isArray(data.plans)) {
      sections.push({
        label: `测试计划进度${data.project ? ` · ${data.project}` : ''}`,
        type: 'kv',
        items: data.plans.slice(0, 8).map((p: any) => ({ k: p.plan, v: `执行${p.executedRate} 通过${p.passRate}` })),
      });
    }
  }
  if (!sections.length) {
    return null;
  }
  // 单一非分布查询不出卡片（LLM 文字已够），有分布或多段才出
  if (!hasBar && sections.length < 2) {
    return null;
  }
  return { title: title || '查询结果', sections };
}

export interface ProjectLite {
  id: number;
  name: string;
}
export type ProjectMatch = { id: number } | { ambiguous: string[] } | { notFound: true };

/**
 * 按名字匹配项目：忽略大小写/空格的精确匹配优先；否则子串匹配，唯一命中才用，
 * 多个命中返回 ambiguous 让上层要求用户澄清。纯函数，便于单测。
 */
export function matchProject(projects: ProjectLite[], name: string): ProjectMatch {
  const norm = (s: any) => (s ?? '').toString().trim().toLowerCase();
  const q = norm(name);
  if (!q) {
    return { notFound: true };
  }
  const exact = projects.find((p) => norm(p.name) === q);
  if (exact) {
    return { id: exact.id };
  }
  const contains = projects.filter((p) => norm(p.name).includes(q));
  if (contains.length === 1) {
    return { id: contains[0].id };
  }
  if (contains.length > 1) {
    return { ambiguous: contains.map((p) => p.name) };
  }
  return { notFound: true };
}

// 缺陷状态/优先级 名→id（系统级稳定枚举，dev 真机核实）。"未关闭"=新创建+处理中+已解决。
export const STATUS_NAME_TO_ID: Record<string, number> = {
  新创建: 1,
  处理中: 3,
  已解决: 5,
  关闭: 6,
  拒绝: 10002,
  挂起: 10003,
};
export const UNCLOSED_STATUS_IDS = [1, 3, 5];
export const PRIORITY_NAME_TO_ID: Record<string, number> = { 最高: 1, 高: 2, 中: 3, 低: 4 };

/** 把状态名（可含"未关闭"）解析成 issueStatus id 数组。纯函数。 */
export function resolveStatusIds(status: string | string[] | undefined): number[] {
  if (!status) {
    return [];
  }
  const arr = Array.isArray(status) ? status : [status];
  const ids: number[] = [];
  for (const s of arr) {
    const t = (s ?? '').toString().trim();
    if (['未关闭', '未解决', '未闭环', 'open', '未处理完'].includes(t)) {
      ids.push(...UNCLOSED_STATUS_IDS);
    } else if (t in STATUS_NAME_TO_ID) {
      ids.push(STATUS_NAME_TO_ID[t]);
    }
  }
  return [...new Set(ids)];
}

/** 把优先级名解析成 priority id 数组。纯函数。 */
export function resolvePriorityIds(priority: string | string[] | undefined): number[] {
  if (!priority) {
    return [];
  }
  const arr = Array.isArray(priority) ? priority : [priority];
  const ids: number[] = [];
  for (const p of arr) {
    const t = (p ?? '').toString().trim();
    if (t in PRIORITY_NAME_TO_ID) {
      ids.push(PRIORITY_NAME_TO_ID[t]);
    }
  }
  return [...new Set(ids)];
}
